import {
  Col,
  Collapse,
  Row,
  Statistic,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import React, { useMemo } from 'react';
import { CrmList } from '@/components/CrmList';
import QuietEmpty from '@/components/QuietEmpty';
import { formatCrmDate } from '@/modules/zy-crm/utils/display';
import type {
  MarketingCampaignAnalysisTouchpointItem,
  MarketingCampaignDailyStatItem,
  MarketingCampaignDetail,
} from '@/services/bff/types/marketing-campaign';
import styles from './style.module.less';

const { Text } = Typography;

type ChartToken = ReturnType<typeof theme.useToken>['token'];

export const UNASSIGNED_TOUCHPOINT_LABEL = '未绑定接入点';

const formatMoney = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return '-';
  }
  return Number(value).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return '-';
  }
  const normalized = Number(value) > 1 ? Number(value) : Number(value) * 100;
  return `${normalized.toFixed(2)}%`;
};

const getSafeNumber = (value?: number | null) =>
  value === undefined || value === null || Number.isNaN(Number(value))
    ? undefined
    : Number(value);

export const calculateRate = (
  numerator?: number | null,
  denominator?: number | null,
) => {
  const safeNumerator = getSafeNumber(numerator);
  const safeDenominator = getSafeNumber(denominator);
  if (
    safeNumerator === undefined ||
    safeDenominator === undefined ||
    safeDenominator <= 0
  ) {
    return undefined;
  }
  return safeNumerator / safeDenominator;
};

const getTrendRows = (detail?: MarketingCampaignDetail) => {
  const rows = detail?.analysis?.trend?.length
    ? detail.analysis.trend
    : detail?.dailyStats || [];
  return [...rows].sort((a, b) =>
    String(a.statDate || '').localeCompare(String(b.statDate || '')),
  );
};

const getTouchpointRows = (detail?: MarketingCampaignDetail) =>
  detail?.analysis?.touchpoints || [];

const getTouchpointLabel = (row: MarketingCampaignAnalysisTouchpointItem) => {
  if (row.touchpointUuid === '__unassigned__') {
    return row.touchpointName || UNASSIGNED_TOUCHPOINT_LABEL;
  }
  return (
    row.touchpointName || row.touchpointUuid || UNASSIGNED_TOUCHPOINT_LABEL
  );
};

export const buildFunnelOption = (
  stages: Array<{ name: string; value: number }>,
  token: ChartToken,
): EChartsOption => ({
  backgroundColor: 'transparent',
  tooltip: { trigger: 'item' },
  series: [
    {
      type: 'funnel',
      left: '8%',
      top: 24,
      bottom: 24,
      width: '84%',
      minSize: '10%',
      maxSize: '100%',
      sort: 'descending',
      gap: 4,
      label: {
        show: true,
        position: 'inside',
        formatter: '{b}: {c}',
      },
      itemStyle: {
        borderColor: token.colorBgContainer,
        borderWidth: 1,
      },
      data: stages.map((stage) => ({ ...stage })),
    },
  ],
});

export const buildTrendOption = (
  rows: MarketingCampaignDailyStatItem[],
  token: ChartToken,
): EChartsOption => ({
  backgroundColor: 'transparent',
  color: [
    token.colorPrimary,
    token.colorSuccess,
    token.colorInfo,
    token.colorWarning,
  ],
  tooltip: { trigger: 'axis' },
  legend: {
    data: ['线索数', '有效线索', '转客户', '赢单金额'],
    textStyle: { color: token.colorText },
  },
  grid: { left: 8, right: 8, top: 40, bottom: 28, containLabel: true },
  xAxis: {
    type: 'category',
    data: rows.map((row) => formatCrmDate(row.statDate, 'date')),
    axisLabel: { color: token.colorTextSecondary },
    axisLine: { lineStyle: { color: token.colorBorder } },
  },
  yAxis: [
    {
      type: 'value',
      name: '数量',
      axisLabel: { color: token.colorTextSecondary },
      nameTextStyle: { color: token.colorTextSecondary },
      splitLine: { lineStyle: { color: token.colorSplit } },
    },
    {
      type: 'value',
      name: '金额',
      axisLabel: { color: token.colorTextSecondary },
      nameTextStyle: { color: token.colorTextSecondary },
      splitLine: { show: false },
    },
  ],
  series: [
    {
      name: '线索数',
      type: 'bar',
      barMaxWidth: 18,
      data: rows.map((row) => Number(row.leadCount || 0)),
    },
    {
      name: '有效线索',
      type: 'line',
      smooth: true,
      data: rows.map((row) => Number(row.validLeadCount || 0)),
    },
    {
      name: '转客户',
      type: 'line',
      smooth: true,
      data: rows.map((row) => Number(row.transformedCustomerCount || 0)),
    },
    {
      name: '赢单金额',
      type: 'line',
      smooth: true,
      yAxisIndex: 1,
      areaStyle: { opacity: 0.08 },
      data: rows.map((row) => Number(row.wonOpportunityAmount || 0)),
    },
  ],
});

export const buildTouchpointOption = (
  rows: MarketingCampaignAnalysisTouchpointItem[],
  token: ChartToken,
): EChartsOption => ({
  backgroundColor: 'transparent',
  animation: false,
  color: [token.colorPrimary, token.colorSuccess],
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: (params) => {
      const list = Array.isArray(params) ? params : [params];
      const first = list[0] as { dataIndex?: number };
      const row = rows[first?.dataIndex ?? 0];
      if (!row) {
        return '';
      }
      const lines = [getTouchpointLabel(row)];
      for (const item of list) {
        const point = item as {
          marker?: string;
          seriesName?: string;
          value?: number;
        };
        lines.push(
          `${point.marker || ''}${point.seriesName || ''}: ${point.value ?? 0}`,
        );
      }
      lines.push(
        `重复 ${Number(row.duplicateLeadCount || 0)}，转商机 ${Number(row.transformedOpportunityCount || 0)}，赢单 ${formatMoney(row.wonOpportunityAmount)}`,
      );
      lines.push(
        `有效率 ${formatPercent(calculateRate(row.validLeadCount, row.leadCount))}，转客户率 ${formatPercent(calculateRate(row.transformedCustomerCount, row.leadCount))}`,
      );
      return lines.join('<br/>');
    },
  },
  legend: {
    data: ['线索数', '转客户数'],
    textStyle: { color: token.colorText },
  },
  grid: { left: 120, right: 24, top: 36, bottom: 28, containLabel: false },
  xAxis: {
    type: 'value',
    axisLabel: { color: token.colorTextSecondary },
    splitLine: { lineStyle: { color: token.colorBorderSecondary } },
  },
  yAxis: {
    type: 'category',
    inverse: true,
    data: rows.map(getTouchpointLabel),
    axisLabel: {
      color: token.colorText,
      width: 105,
      overflow: 'truncate',
    },
    axisLine: { show: false },
    axisTick: { show: false },
  },
  dataZoom:
    rows.length > 12
      ? [
          {
            type: 'inside',
            yAxisIndex: 0,
            start: 0,
            end: (12 / rows.length) * 100,
          },
        ]
      : [],
  series: [
    {
      name: '线索数',
      type: 'bar',
      barMaxWidth: 18,
      data: rows.map((row) => Number(row.leadCount || 0)),
    },
    {
      name: '转客户数',
      type: 'bar',
      barMaxWidth: 18,
      data: rows.map((row) => Number(row.transformedCustomerCount || 0)),
    },
  ],
});

const TREND_TOOLTIP =
  '概览指标与漏斗为实时统计；趋势为快照，仅在线索导入、线索转客户后刷新，商机赢单或丢失不会更新它。';

export function MarketingCampaignStatsSection({
  detail,
}: {
  detail?: MarketingCampaignDetail;
}) {
  const { token } = theme.useToken();

  const overview = detail?.analysis?.overview;
  const totalLeads = Number(overview?.totalLeads ?? detail?.leadCount ?? 0);
  const validLeadCount = Number(
    overview?.validLeadCount ?? detail?.validLeadCount ?? 0,
  );
  const duplicateLeadCount = Number(
    overview?.duplicateLeadCount ?? detail?.duplicateLeadCount ?? 0,
  );
  const transformedCustomerCount = Number(
    overview?.transformedCustomerCount ?? detail?.transformedCustomerCount ?? 0,
  );
  const transformedOpportunityCount = Number(
    overview?.transformedOpportunityCount ??
      detail?.transformedOpportunityCount ??
      0,
  );
  const wonOpportunityAmount = getSafeNumber(
    overview?.wonOpportunityAmount ?? detail?.wonOpportunityAmount,
  );
  const budgetAmount = getSafeNumber(detail?.budgetAmount);

  const validLeadRate =
    getSafeNumber(overview?.validLeadRate) ??
    calculateRate(validLeadCount, totalLeads);
  const duplicateLeadRate =
    getSafeNumber(overview?.duplicateLeadRate) ??
    calculateRate(duplicateLeadCount, totalLeads);
  const transformedCustomerRate =
    getSafeNumber(overview?.transformedCustomerRate) ??
    calculateRate(transformedCustomerCount, totalLeads);
  const transformedOpportunityRate =
    getSafeNumber(overview?.transformedOpportunityRate) ??
    calculateRate(transformedOpportunityCount, totalLeads);
  const firstFollowUpTimelyRate = getSafeNumber(
    overview?.firstFollowUpTimelyRate,
  );

  const costPerLead =
    getSafeNumber(overview?.costPerLead) ??
    (budgetAmount !== undefined
      ? calculateRate(budgetAmount, totalLeads)
      : undefined);
  const costPerOpportunity =
    getSafeNumber(overview?.costPerOpportunity) ??
    (budgetAmount !== undefined
      ? calculateRate(budgetAmount, transformedOpportunityCount)
      : undefined);
  const roi =
    budgetAmount !== undefined && budgetAmount > 0
      ? calculateRate(wonOpportunityAmount, budgetAmount)
      : undefined;

  const trendRows = useMemo(() => getTrendRows(detail), [detail]);
  const touchpointRows = useMemo(() => getTouchpointRows(detail), [detail]);

  const funnelOption = useMemo(
    () =>
      buildFunnelOption(
        [
          { name: '累计线索', value: totalLeads },
          { name: '有效线索', value: validLeadCount },
          { name: '转客户', value: transformedCustomerCount },
          { name: '转商机', value: transformedOpportunityCount },
        ],
        token,
      ),
    [
      token,
      totalLeads,
      validLeadCount,
      transformedCustomerCount,
      transformedOpportunityCount,
    ],
  );

  const trendOption = useMemo(
    () => buildTrendOption(trendRows, token),
    [trendRows, token],
  );

  const touchpointOption = useMemo(
    () => buildTouchpointOption(touchpointRows, token),
    [touchpointRows, token],
  );

  const rateItems = [
    `有效率 ${formatPercent(validLeadRate)}`,
    `重复率 ${formatPercent(duplicateLeadRate)}`,
    `转客户率 ${formatPercent(transformedCustomerRate)}`,
    `转商机率 ${formatPercent(transformedOpportunityRate)}`,
    firstFollowUpTimelyRate === undefined
      ? null
      : `首次跟进及时率 ${formatPercent(firstFollowUpTimelyRate)}`,
  ].filter((item): item is string => Boolean(item));

  const showDailyDetail = trendRows.length > 0;

  return (
    <div className={styles.stack}>
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={10}>
          <div className={styles.block}>
            <Text strong>转化漏斗</Text>
            {totalLeads > 0 ? (
              <>
                <ReactECharts
                  notMerge
                  option={funnelOption}
                  style={{ height: 240 }}
                />
                <Text type="secondary">{rateItems.join(' · ')}</Text>
              </>
            ) : (
              <QuietEmpty description="活动暂无线索，关联或导入线索后查看转化分析" />
            )}
          </div>
        </Col>
        <Col xs={24} lg={14}>
          <div className={styles.block}>
            <Text strong>每日趋势</Text>
            {trendRows.length ? (
              <>
                <ReactECharts
                  notMerge
                  option={trendOption}
                  style={{ height: 260 }}
                />
                {detail?.analysis?.updatedAt ? (
                  <Tooltip title={TREND_TOOLTIP}>
                    <Text
                      type="secondary"
                      className={styles.caption}
                    >{`趋势数据更新于 ${formatCrmDate(detail.analysis.updatedAt)}`}</Text>
                  </Tooltip>
                ) : null}
              </>
            ) : (
              <QuietEmpty description="暂无趋势数据，导入线索后自动按天统计" />
            )}
          </div>
        </Col>
      </Row>

      {budgetAmount !== undefined && budgetAmount > 0 ? (
        <div className={styles.block}>
          <Text strong>成本效率</Text>
          <Row gutter={[12, 12]} className={styles.costRow}>
            <Col xs={24} sm={8}>
              <Statistic title="单线索成本" value={formatMoney(costPerLead)} />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="单商机成本"
                value={formatMoney(costPerOpportunity)}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="赢单投产比"
                value={roi === undefined ? '-' : `${roi.toFixed(2)}×`}
              />
            </Col>
          </Row>
        </div>
      ) : null}

      <div className={styles.block}>
        <Text strong>接入点对比（按最后触点归因）</Text>
        {touchpointRows.length ? (
          <ReactECharts
            notMerge
            option={touchpointOption}
            style={{
              height: Math.max(
                220,
                Math.min(touchpointRows.length, 12) * 26 + 60,
              ),
            }}
          />
        ) : (
          <QuietEmpty description="暂无线索归因数据，关联接入点后按渠道对比表现" />
        )}
      </div>

      {showDailyDetail ? (
        <Collapse
          ghost
          items={[
            {
              key: 'daily',
              label: '每日明细',
              children: (
                <CrmList
                  dataSource={trendRows}
                  renderItem={(item) => (
                    <CrmList.Item>
                      <CrmList.Item.Meta
                        title={formatCrmDate(item.statDate, 'date')}
                        description={`线索 ${item.leadCount || 0}，有效 ${item.validLeadCount || 0}，转客户 ${item.transformedCustomerCount || 0}，转商机 ${item.transformedOpportunityCount || 0}`}
                      />
                      <Text>{formatMoney(item.wonOpportunityAmount)}</Text>
                    </CrmList.Item>
                  )}
                />
              ),
            },
          ]}
        />
      ) : null}
    </div>
  );
}
